import { Component, OnDestroy, OnInit } from '@angular/core';
import { distinctUntilChangedWithSideEffectOnLike } from '@app/core/rxjs/rxOperators';
import { ColumnShown } from '@app/library/models/column-shown.model';
import { ArchivedTrack } from '@app/library/models/trash.model';
import { SearchService } from '@app/library/services/search.service';
import { UnselectAll } from '@app/library/states/selected-tracks.actions';
import { SetMobileTrashColumns } from '@app/library/states/trash-columns.actions';
import { TrashColumnsState } from '@app/library/states/trash-columns.state';
import {
  ArchivedTracksFailure,
  ArchivedTracksRequest,
  ArchivedTracksSuccess,
} from '@app/library/states/trash.actions';
import { TrashState } from '@app/library/states/trash.state';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select, Store } from '@ngxs/store';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { isArray } from 'lodash';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, shareReplay } from 'rxjs/operators';

@Component({
  selector: 'rk-trash-all',
  templateUrl: './trash-all.component.html',
  styleUrls: ['./trash-all.component.scss'],
})
export class TrashAllComponent implements OnInit, OnDestroy {
  @Select(TrashColumnsState.getColumnShow)
  columns$: Observable<ColumnShown>;

  @Select(TrashState.archivedTracks)
  archivedTracks$: Observable<ArchivedTrack[]>;

  searchedArchivedTracks$: Observable<ArchivedTrack[]>;

  isLoading$: Observable<boolean>;

  searchSubject = new BehaviorSubject<string>('');

  subscriptions = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly searchService: SearchService,
    private readonly helper: UiStoreHelperService,
  ) {}

  ngOnInit(): void {
    this.setMobileColumns(window.innerWidth);
    this.subscriptions.add(
      fromEvent(window, 'resize')
        .pipe(debounceTime(100))
        .subscribe(w => {
          if (w && w.target) {
            this.setMobileColumns((w.target as Window).innerWidth);
          }
        }),
    );

    // Loading state
    this.isLoading$ = this.helper.getLoadingState(
      ArchivedTracksRequest,
      [ArchivedTracksFailure, ArchivedTracksSuccess],
      true,
    );

    // Search archived tracks
    this.searchedArchivedTracks$ = this.searchService
      .createTracksSearch(this.archivedTracks$, this.searchSubject.asObservable())
      .pipe(
        filter(val => isArray(val)),
        distinctUntilChangedWithSideEffectOnLike(() =>
          this.store.dispatch(new UnselectAll()),
        ),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

    // Fetch archived tracks from current radio
    this.getArchivedTracks();

    this.store.dispatch(new UnselectAll());
  }

  setMobileColumns(w: number) {
    if (w < 1081) {
      this.store.dispatch(new SetMobileTrashColumns());
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @Dispatch()
  getArchivedTracks() {
    return new ArchivedTracksRequest();
  }

  onChangeSearch(str: string) {
    if (typeof str === 'string') {
      this.searchSubject.next(str);
    }
  }
}
